<template>
  <div
    class="w-full flex justify-center items-center p-2 min-h-14 text-n-0 font-body transition-colors rounded-lg"
    :class="[
      disabled ? 'cursor-default' : 'cursor-pointer',
      {
        'pointer-events-none': disabled,
        'bg-[#5D5C8C]': isSelected
      }
    ]"
    @click="$emit('selected')"
  >
    <div class="w-full flex justify-center items-center gap-3">
      <l-checkbox
        :model-value="isSelected"
        @update:model-value="$emit('selected')"
        :disabled="disabled"
      />

      <div class="flex justify-between items-center w-full gap-1 min-w-0">
        <div
          class="flex flex-col font-heading text-sm font-medium truncate"
          :class="{
            'opacity-60': product.paid || (disabled && !isSelected)
          }"
        >
          <div>{{ product.name }}</div>
          <div v-if="description" class="text-n-200">
            {{ description }}
          </div>
          <div v-if="product.discountType === '2x1'" class="text-g-500 text-sm">
            <!-- eslint-disable-next-line -->
            {{ '2x1' }}
            <span v-if="product.quantity > 1"> ({{ product.discountAmount }}) </span>
          </div>
        </div>

        <div class="font-bold font-text text-xs flex items-center gap-3 shrink-0">
          <div
            v-if="product.billCode"
            class="bg-[#39C0FE] rounded h-5 flex px-2 body-4 items-center shrink-0"
          >
            {{ product.billCode }}
          </div>
          <div
            v-if="product.partialPaid"
            class="bg-[#E5AE00] rounded h-5 flex px-2 body-4 items-center shrink-0"
          >
            {{ t('checkout.partial-paid') }}
          </div>
          <div
            v-if="product.paid"
            class="bg-[#1FAD9F] rounded h-5 flex px-2 body-4 items-center shrink-0"
          >
            {{ t('checkout.paid') }}
          </div>
          <div
            class="flex flex-col"
            :class="{
              'opacity-60': product.paid || (disabled && !isSelected)
            }"
          >
            <div v-if="product.discountType" class="font-body text-xs line-through">
              <span>
                {{ currency(product.fullPrice) }}
              </span>
            </div>
            <div v-if="product.discountType === '2x1'">
              {{ currency(product.fullPrice * (product.quantity - product.discountAmount!)) }}
            </div>
            <div v-else>
              {{ currency(product.finalPrice) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { toRef } from 'vue'
import { useI18n } from 'vue-i18n'

import { LCheckbox } from '@last/core-ui/paprika'

import { useMoney } from '@/composables/useMoney'
import useProduct from '@/composables/useProduct'
import type { TabProduct } from '@/types'

const { t } = useI18n()

type Props = {
  product: TabProduct & {
    billId: string | null
    billCode: string | null
    partialPaid: boolean
    paid: boolean
  }
  isSelected?: boolean
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isSelected: false,
  disabled: false
})

defineEmits<{
  selected: []
}>()

const { currency } = useMoney()

const product = toRef(props, 'product')

const { description } = useProduct(product)
</script>
